import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';
import { clarityService } from '../../services/clarityService';
import { analyticsService } from '../../services/analyticsService';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Identify user in analytics
      if (session?.user) {
        // Identify user in Clarity
        clarityService.identify(session.user.id, undefined, undefined);
        // Identify user in Google Analytics
        analyticsService.identify(session.user.id, {
          email: session.user.email,
          full_name: session.user.user_metadata?.full_name
        });
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Identify user in analytics
      if (session?.user) {
        // Identify user in Clarity
        clarityService.identify(session.user.id, undefined, undefined);
        // Identify user in Google Analytics
        analyticsService.identify(session.user.id, {
          email: session.user.email,
          full_name: session.user.user_metadata?.full_name
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async (redirectPath: string = '/simulations') => {
    const hasWindow = typeof window !== 'undefined';
    const options: {
      redirectTo?: string;
      skipBrowserRedirect?: boolean;
    } = {};

    if (hasWindow) {
      window.sessionStorage.setItem('postAuthRedirect', redirectPath);
      options.redirectTo = `${window.location.origin}/auth/callback`;
    } else {
      options.skipBrowserRedirect = true;
      console.warn('Google sign-in attempted in a non-browser environment. Redirect URL returned instead.');
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options,
    });

    return { error };
  };

  const signOut = async () => {
    // Use scope: 'local' to only clear the local session
    // This avoids 403 errors when the server session is not found
    await supabase.auth.signOut({ scope: 'local' });
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithGoogle, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

